import * as React from 'react';

import {
  enumLoadStatus,
  enumSalesLoadLineItemPhase,
} from '@rematter/frontend-lib-api-client';
import { Button, cn, Footer } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';
import { LucidePlusCircle } from 'lucide-react';

import { BrokeredLoadShippedTable } from '../../_shared/LineItems';

import { LoadShippedMaterialsFooter } from './LoadShippedMaterialsFooter';

import { $brokeredLoadForm } from '~/modules/brokerage/_state/brokeredLoadDetail.state';
import { useBrokeredLoadLineItemsManager } from '~/modules/brokerage/hooks/useBrokeredLoadLineItemsManager';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import {
  BrokeredLoadFormState,
  BrokeredLoadRecord,
} from '~/modules/brokerage/types/brokeredLoad';
import { WeightUnitSwitch } from '~/modules/trade/_components/_shared/WeightUnitSwitch/WeightUnitSwitch';
import { WeightUnitType } from '~/modules/trade/types/load';

type Props = {
  load: BrokeredLoadRecord;
  onDiscard?: () => void;
  onSaveUpdates?: () => void;
  isSaving?: boolean;
};

const loadMaterialsFormSelector = (state: BrokeredLoadFormState) => ({
  lineItems: state.lineItems,
});

export const LoadShippedMaterials: React.FC<Props> = ({
  load,
  onDiscard,
  onSaveUpdates,
  isSaving = false,
}) => {
  const t = useTranslations();
  const [weightUnit, setWeightUnit] = React.useState<WeightUnitType>('price');
  const selectedFormAtom = React.useMemo(
    () => selectAtom($brokeredLoadForm, loadMaterialsFormSelector, isEqual),
    [],
  );
  const form = useAtomValue(selectedFormAtom);

  const requestedItems = React.useMemo(
    () =>
      form.lineItems.filter(
        (item) =>
          !item.phase || item.phase === enumSalesLoadLineItemPhase.Requested,
      ),
    [form.lineItems],
  );

  const shippedItems = React.useMemo(
    () =>
      form.lineItems.filter(
        (item) => item.phase === enumSalesLoadLineItemPhase.Shipped,
      ),
    [form.lineItems],
  );

  const {
    addItem,
    updateItem,
    deleteItem,
    hasUnaddedRequestedItems,
    addLineItemsFromRequested,
  } = useBrokeredLoadLineItemsManager({ requestedItems, shippedItems });

  const isEditable =
    load.status === enumLoadStatus.Pending ||
    load.status === enumLoadStatus.Unassigned;

  const handleAddShippedItem = React.useCallback(() => {
    addItem({ phase: enumSalesLoadLineItemPhase.Shipped });
  }, [addItem]);

  return (
    <>
      <div className="flex-1 overflow-auto px-5 py-6 pr-4">
        <div className="mb-2">
          <WeightUnitSwitch value={weightUnit} onChange={setWeightUnit} />
        </div>
        <BrokeredLoadShippedTable
          lineItems={shippedItems}
          onAddLineItem={handleAddShippedItem}
          onUpdateLineItem={updateItem}
          onDeleteLineItem={deleteItem}
          weightUnit={weightUnit}
        />

        {isEditable && (
          <div
            className={cn(
              'mt-4 flex gap-3',
              !shippedItems.length && 'justify-center',
            )}
          >
            <Button
              variant="outline"
              color="neutral"
              icon={<LucidePlusCircle />}
              onPress={handleAddShippedItem}
            >
              {t.brokerage.loads.detail.materials.addMaterial}
            </Button>

            {hasUnaddedRequestedItems && (
              <Button
                variant="outline"
                color="neutral"
                icon={<LucidePlusCircle />}
                onPress={addLineItemsFromRequested}
              >
                {t.brokerage.loads.detail.materials.addAllFromRequested}
              </Button>
            )}
          </div>
        )}
      </div>

      <Footer className="flex-shrink-0" displayShadow>
        <div className="flex items-center justify-end gap-5 px-5 py-2.5">
          <LoadShippedMaterialsFooter
            lineItems={shippedItems}
            weightUnit={weightUnit}
          />

          <div className="ml-4 flex items-center justify-end gap-5">
            <Button
              color="neutral"
              variant="outline"
              onPress={() => onDiscard?.()}
              isDisabled={isSaving}
            >
              {t.brokerage.loads.detail.footer.discard}
            </Button>
            <Button
              color="primary"
              variant="solid"
              onPress={() => onSaveUpdates?.()}
              isPending={isSaving}
              isLoading={isSaving}
            >
              {t.brokerage.loads.detail.footer.saveUpdates}
            </Button>
          </div>
        </div>
      </Footer>
    </>
  );
};
